import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import NoiseOverlay from '../components/ui/NoiseOverlay';
import Marquee from '../components/ui/Marquee';
import LoadingScreen from '../components/ui/LoadingScreen';
import ElectricBorder from '../components/ui/ElectricBorder';
import { REALM_ORDER, THEMES } from '../lib/themes';
import api from '../lib/api';
import { trackPageView } from '../utils/analytics';

const SEEN_KEY = 'owlfit_seen_intro_v2';

const HomePage = () => {
  const [loading, setLoading] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !sessionStorage.getItem(SEEN_KEY);
  });
  const [liveRealms, setLiveRealms] = useState([]);

  useEffect(() => {
    trackPageView('home');
  }, []);

  // Detect which realms have live products
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/products');
        if (cancelled) return;
        const products = res.data?.data || [];
        const set = new Set(
          products.filter((p) => p.isActive && p.inStock).map((p) => p.category)
        );
        setLiveRealms(Array.from(set));
      } catch {
        // If backend is down, assume only anime is live (existing behavior)
        setLiveRealms(['anime']);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <LoadingScreen
        onComplete={() => {
          sessionStorage.setItem(SEEN_KEY, '1');
          setLoading(false);
        }}
      />
    );
  }

  const isComingSoon = (id) => !liveRealms.includes(id);

  const REALM_ELECTRIC = {
    anime: '#ec4899',
    tantra: '#ef4444',
    inkspeak: '#e8b06a',
    soul: '#818cf8',
  };

  return (
    <div className="min-h-screen bg-neutral-100 flex flex-col relative overflow-hidden">
      <NoiseOverlay />

      <Marquee
        text="EST. 2024 /// OWLFIT STREETWEAR /// SELECT YOUR REALM /// WORLDWIDE SHIPPING ON ALL DROPS /// "
        className="bg-black text-white py-2 font-mono font-bold text-sm border-b-4 border-white z-30"
      />

      <div className="flex-grow flex flex-col md:flex-row relative">
        <div className="absolute top-0 left-0 w-full p-6 z-20 flex justify-between items-start pointer-events-none">
          <div className="pointer-events-auto">
            <div className="flex items-center border-2 border-black bg-[#15132b] shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-150 h-9">
              {/* Barcode stub */}
              <div className="flex items-center gap-1.5 bg-white h-full px-2 border-r-2 border-dashed border-black">
                <div className="flex items-stretch gap-[2px] h-5">
                  {[2, 1, 3, 1, 1, 2, 1, 3, 2, 1].map((w, i) => (
                    <span key={i} className="bg-black h-full" style={{ width: `${w}px` }} />
                  ))}
                </div>
              </div>

              {/* Inline label */}
              <div className="flex items-center gap-2 px-3 h-full">
                <span className="text-pink-500 font-black text-sm tracking-tight uppercase animate-glitch">CHOOSE_YOUR_VIBE</span>
                <span className="bg-pink-500 text-white font-black text-[9px] px-1 py-0.5 tracking-widest uppercase">.EXE</span>
                <span className="hidden md:flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-pink-500" />
                  <span className="w-2 h-2 bg-pink-500" />
                </span>
              </div>
            </div>
          </div>

          <Link
            to="/blog"
            className="bg-white border-4 border-black px-4 md:px-6 py-2 md:py-3 font-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all pointer-events-auto uppercase tracking-wider text-xs md:text-base"
          >
            Archives / Lore
          </Link>
        </div>

        {REALM_ORDER.map((key) => {
          const t = THEMES[key];
          const soon = isComingSoon(key);
          return (
            <Link
              key={key}
              to={`/${key}`}
              className={`group relative flex-1 h-[25vh] md:h-auto border-b-4 md:border-b-0 md:border-r-4 border-black cursor-pointer transition-all duration-300 ease-out hover:flex-[1.5] overflow-hidden ${t.colors.bg}`}
            >
              {t.bgImage && (
                <>
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url('${t.bgImage}')` }}
                  />
                  {/* Light scrim, fades further on hover */}
                  <div className="absolute inset-0 bg-black/15 group-hover:bg-black/0 transition-colors duration-300 pointer-events-none" />
                  {/* Shine sweep on select */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-0 -left-1/2 w-1/2 h-full bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-0 group-hover:animate-shine" />
                  </div>
                </>
              )}

              {/* Electric border on select (all realms) */}
              <ElectricBorder
                color={REALM_ELECTRIC[key]}
                className="z-[25] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              />

              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none overflow-hidden">
                <div className="transform scale-[8] animate-spin-slow opacity-10 text-black">
                  {t.icon}
                </div>
                <div className="absolute top-10 left-10 font-black text-9xl opacity-5 rotate-90 whitespace-nowrap">
                  {t.label} {t.label}
                </div>
              </div>

              {soon && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                  <div className={`px-4 md:px-8 py-3 md:py-4 border-4 ${t.colors.border} ${t.colors.accent} transform rotate-3 shadow-[8px_8px_0px_rgba(0,0,0,1)]`}>
                    <span className={`text-lg md:text-2xl font-black uppercase tracking-widest ${t.id === 'tantra' ? 'text-black' : t.id === 'inkspeak' ? 'text-white' : 'text-indigo-900'}`}>
                      COMING SOON
                    </span>
                  </div>
                </div>
              )}

              <div className={`relative z-10 h-full flex flex-row md:flex-col items-center justify-center md:justify-between p-6 group-hover:scale-105 transition-transform ${soon ? 'opacity-50' : ''}`}>
                <div className="hidden md:block w-12 h-12 invisible"></div>
                <div className="flex flex-col items-center">
                  <div className="mb-6 transform group-hover:rotate-12 transition-transform duration-300 bg-black p-4 text-white border-2 border-white shadow-[4px_4px_0px_rgba(0,0,0,0.4)]">
                    {React.cloneElement(t.icon, { size: 40 })}
                  </div>
                  <div className="relative px-5 py-2 bg-black/40 backdrop-blur-md border-2 border-white/25 shadow-[5px_5px_0px_rgba(0,0,0,0.6)] group-hover:border-white/60 group-hover:shadow-[7px_7px_0px_rgba(0,0,0,0.7)] transition-all duration-300">
                    <span className={`absolute -top-px left-0 right-0 h-1 ${t.colors.accent} group-hover:animate-pulse`} />
                    <h2
                      className={`text-4xl md:text-6xl lg:text-7xl font-black text-center uppercase ${t.font} text-white leading-[0.8] group-hover:animate-glitch`}
                      style={{
                        textShadow:
                          '2px 2px 0 #000, -2px 2px 0 #000, 2px -2px 0 #000, -2px -2px 0 #000, 0 0 18px rgba(0,0,0,0.9)',
                      }}
                    >
                      {t.label}
                    </h2>
                  </div>
                  <span className="mt-4 px-3 py-1 bg-black text-white text-xs font-bold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity border border-white">
                    {soon ? 'Stay Tuned' : 'Enter Realm'}
                  </span>
                </div>
                <div className="hidden md:block text-xs font-bold tracking-widest uppercase bg-white px-2 py-1 border border-black whitespace-nowrap">
                  {soon ? 'SOON™' : t.vibe}
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <Marquee
        text="/// NEW COLLECTION DROPPING FRIDAY /// LIMITED STOCK /// DO NOT SLEEP /// "
        className="bg-yellow-400 text-black py-2 font-black text-lg border-t-4 border-black z-30"
        speed="15s"
      />
    </div>
  );
};

export default HomePage;
